import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { postBusinessPrimary } from '~/api';
import { ADD_BUSINESS_ROUTE, BILLING_ROUTE } from '~/constants/routes';
import { useBoolean } from '~/hooks/useBoolean';
import AddIcon from '~/icons/AddIcon';
import BillingIcon from '~/icons/BillingIcon';
import DoubleArrowIcon from '~/icons/DoubleArrowIcon';
import LogoutIcon from '~/icons/LogoutIcon';
import TickIcon from '~/icons/TickIcon';
import UpgradeIcon from '~/icons/UpgradeIcon';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { hideLoader, showLoader } from '~/store/slices/app';
import { logOut, selectCompany } from '~/store/slices/user';
import { RootState } from '~/types';
import { IBusiness } from '~/types/business';
import { getScreenNameFromPath } from '~/utils/analytics';
import { apiResponseHandler } from '~/utils/api';
import ClickOutside from './ClickOutside';
import Gap from './Gap';
import Text from './Text';

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  border: 1px solid var(--border1);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--neut7);
  }
`;

const CompanyInitial = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #f7f5ff;
  border: 1px solid var(--border1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--t-black);
  font-weight: 600;
  font-size: 18px;
`;

const ProfileWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownContainer = styled.div`
  position: absolute;
  width: 100%;
  background: white;
  border: 1px solid var(--border1);
  border-radius: 8px;
  left: 100%;
  bottom: 0;
  margin-left: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1000;
`;

const UserSection = styled.div`
  text-align: center;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border1);
`;

const CompanySection = styled.div`
  padding: 10px 0 12px 0;
`;

const CompanyList = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  max-height: 200px;
  overflow-y: auto;
`;

const PaddedSection = styled.div`
  padding: 0 10px;
`;

const CompanyItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--neut7);
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AddCompanyLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 4px;
  border: 1px solid var(--border1);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--neut3);
  text-decoration: none;

  &:hover {
    background: var(--neut7);
    border-color: var(--prim4);
    color: var(--prim4);
  }
`;

const ActionsSection = styled.div`
  border-top: 1px solid var(--border1);
`;

const BillingButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--neut3);
  text-decoration: none;
  padding: 10px 16px;

  &:hover {
    background: var(--neut7);
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  border-radius: 0 0 6px 6px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--neut3);
  padding: 10px 16px;

  &:hover {
    background: var(--neut7);
  }
`;

const EmailText = styled(Text)`
  max-width: 95%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlanBadge = styled.div<{ plan: string }>`
  display: flex;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${props => {
    switch (props.plan.toLowerCase()) {
      case 'pro':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'infinity':
        return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      default:
        return 'linear-gradient(135deg, #2e928dff 0%, #91435bff 100%)';
    }
  }};
  color: white;
  font-size: 10px;
`;

interface IUserProfileProps {
  isActionEnabled?: boolean;
}

const UserProfile = ({ isActionEnabled = true }: IUserProfileProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { email, currentPlan, companies, businessDetails } = useSelector(
    (state: RootState) => state.user,
  );
  const [isDialogOpen, dialogOptions] = useBoolean();

  function handleDialogOff() {
    if (isActionEnabled) {
      dialogOptions.off();
    }
  }

  function handleDialogToggle() {
    if (isActionEnabled) {
      dialogOptions.toggle();
    }
  }

  const handleLogout = () => {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: 'Log out',
      link_id: 'log_out_link',
      component_type: 'profile_popup',
    });
    dispatch(logOut());
    handleDialogOff();
  };

  async function handleCompanySelect(companyUuid: string) {
    dispatch(showLoader());
    const promise = await postBusinessPrimary(companyUuid);
    await apiResponseHandler<IBusiness>(promise);
    dispatch(hideLoader());
    dispatch(selectCompany(companyUuid));
    window.location.reload();
  }

  const getCompanyInitial = (companyName: string) => {
    return companyName.charAt(0).toUpperCase();
  };

  const getCurrentCompanyInitial = () => {
    return businessDetails.first_name ? businessDetails.first_name.charAt(0).toUpperCase() : 'U';
  };

  const formatPlanName = (plan: string | null) => {
    if (!plan) return 'Free Plan';
    return plan.charAt(0).toUpperCase() + plan.slice(1) + ' Plan';
  };

  const handleUpgradePlanClick = () => {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: 'Upgrade Plan',
      link_id: 'upgrade_link',
      component_type: 'profile_popup',
    });
    handleDialogOff();
  };

  const handlePlansAndBillingClick = () => {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: 'Plans & Billing',
      link_id: 'plans_and_billing_link',
      component_type: 'profile_popup',
    });
    handleDialogOff();
  };

  return (
    <ClickOutside onClose={handleDialogOff}>
      <ProfileWrapper>
        <ProfileButton onClick={handleDialogToggle}>
          <div className="align-center">
            <CompanyInitial>{getCurrentCompanyInitial()}</CompanyInitial>
            <Gap r={12} />
            <div>
              <Text size="sm" align="left">
                {businessDetails.first_name || 'User'}
              </Text>
              <PlanBadge plan={currentPlan || 'free'}>{formatPlanName(currentPlan)}</PlanBadge>
            </div>
          </div>
          {isActionEnabled && <DoubleArrowIcon />}
        </ProfileButton>

        {isDialogOpen && (
          <DropdownContainer>
            <UserSection>
              <Text size="xs" variant="neutral" className="semi-bold">
                Logged in as
              </Text>
              <Gap t={4} />
              <EmailText size="sm">{email}</EmailText>
            </UserSection>

            <CompanySection>
              <PaddedSection>
                <Text size="xs" variant="neutral" className="semi-bold">
                  Switch Business
                </Text>
              </PaddedSection>

              <CompanyList>
                {companies.map(company => {
                  const isSelected = company.business_uuid === businessDetails.business_uuid;
                  return (
                    <CompanyItem
                      key={company.business_uuid}
                      isSelected={isSelected}
                      onClick={() => handleCompanySelect(company.business_uuid)}
                    >
                      <CompanyInfo>
                        <CompanyInitial>{getCompanyInitial(company.company_name)}</CompanyInitial>
                        <Text size="sm">{company.company_name}</Text>
                      </CompanyInfo>
                      {isSelected && <TickIcon stroke="var(--prim4)" />}
                    </CompanyItem>
                  );
                })}
              </CompanyList>

              <PaddedSection>
                <AddCompanyLink
                  to={
                    currentPlan === 'free' ? `${BILLING_ROUTE}?ref=businesses` : ADD_BUSINESS_ROUTE
                  }
                  onClick={handleDialogOff}
                >
                  {currentPlan === 'free' ? <UpgradeIcon /> : <AddIcon />}
                  <Text size="sm">Add Business</Text>
                </AddCompanyLink>
              </PaddedSection>
            </CompanySection>

            <ActionsSection>
              {currentPlan !== 'infinity' && (
                <BillingButton to={BILLING_ROUTE} onClick={handleUpgradePlanClick}>
                  <UpgradeIcon />
                  <Text size="sm">Upgrade Plan</Text>
                </BillingButton>
              )}

              <BillingButton to={BILLING_ROUTE} onClick={handlePlansAndBillingClick}>
                <BillingIcon />
                <Text size="sm">Plans & Billing</Text>
              </BillingButton>

              <LogoutButton onClick={handleLogout}>
                <LogoutIcon />
                <Text size="sm">Log out</Text>
              </LogoutButton>
            </ActionsSection>
          </DropdownContainer>
        )}
      </ProfileWrapper>
    </ClickOutside>
  );
};

export default UserProfile;
